import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Helpers from '../../utils/helpers';

export default class Tooltip extends React.Component {
	constructor (props) {
		super(props);
		this.elem = null;
		this.setUpRef = this.setUpRef.bind(this);
		this.handleMouseEnter = this.handleMouseEnter.bind(this);
		this.handleMouseLeave = this.handleMouseLeave.bind(this);
		this.getTooltipPosition = this.getTooltipPosition.bind(this);
		this.toolTipBox = Helpers.getTooltipBox();
		this.getTheElementHavingEllipsis = this.getTheElementHavingEllipsis.bind(this);
		this.havingEllipsis = this.havingEllipsis.bind(this);
		this.handleTooltipForTruncatedElement = this.handleTooltipForTruncatedElement.bind(this);
	}

	setUpRef (e) {
		if (!this.elem) {
			this.elem = e;
		}
	}

	getTooltipPosition (toolTipBox) {
		const {placement, chevron} = this.props;
		const elem = this.elem;
		const elemRect = elem.getBoundingClientRect();
		const verticalCenter = elemRect.top + elem.offsetHeight / 2;
		const horizontalCenter = elemRect.left + elem.offsetWidth / 2;
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
		const offsetMargin = chevron ? '0.75rem' : '0.5rem';
		let relTop, relLeft;
		if (placement === 'top') {
			relTop = (scrollTop + elemRect.top - toolTipBox.offsetHeight) + 'px - ' + offsetMargin;
			relLeft = horizontalCenter - toolTipBox.offsetWidth / 2 + 'px';
		} else if (placement === 'right') {
			relTop = scrollTop + verticalCenter - toolTipBox.offsetHeight / 2 + 'px';
			relLeft = (elemRect.left + elem.offsetWidth + window.pageXOffset) + 'px + ' + offsetMargin;
		} else if (placement === 'left') {
			relTop = scrollTop + verticalCenter - toolTipBox.offsetHeight / 2 + 'px';
			relLeft = ( (elemRect.left + window.pageXOffset) - toolTipBox.offsetWidth) + 'px - ' + offsetMargin;
		} else {
			relTop = (scrollTop + elemRect.top + elem.offsetHeight) + 'px + ' + offsetMargin;

			/* Handling the corner cases. Tooltip should not be overflow outside of the container */

			let calculatedPixel = horizontalCenter - toolTipBox.offsetWidth / 2;
			if(calculatedPixel < 0) {
				calculatedPixel = 0;
			}

			relLeft = calculatedPixel + 'px';
		}
		return {
			top: relTop,
			left: relLeft
		};
	}

	handleMouseEnter () {
		const { chevron, placement } = this.props;
        let showTooltip = this.handleTooltipForTruncatedElement();

        if (this.props.show && showTooltip) {
            this.toolTipBox.firstChild.innerHTML = this.props.content;
            this.toolTipBox.classList.add('tooltip-shown');
            this.toolTipBox.classList.remove('hidden');
            this.toolTipBox.lastChild.classList.remove('chevron-top', 'chevron-right', 'chevron-bottom', 'chevron-left');
            if (chevron) {
                this.toolTipBox.lastChild.classList.add(`chevron-${placement}`);
            }
            const toolTipPosition = this.getTooltipPosition(this.toolTipBox);
            this.toolTipBox.setAttribute('style', `top: calc(${toolTipPosition.top}); left: calc(${toolTipPosition.left})`);
        }
	}

    handleMouseLeave () {
		this.toolTipBox.classList.add('hidden');
		this.toolTipBox.classList.remove('tooltip-shown');
	}

    componentWillUnmount (){
        this.handleMouseLeave ();
    }

    /**
     * if any element which has ellipsis and its offsetWidth is less than the actual width of text (scrollWidth), Tooltip should not be shown
     * @returns {boolean}
     */
    handleTooltipForTruncatedElement(){
        let showTooltip = true;
        if (this.props.showOnlyForTruncatedText) {
            // get the child element of tooltip which has ellipsis
            const elementHavingEllipsis = this.getTheElementHavingEllipsis(this.elem);
            showTooltip = elementHavingEllipsis && elementHavingEllipsis.offsetWidth < elementHavingEllipsis.scrollWidth;
        }
        return showTooltip;
    }

    /**
     * Check if element has ellipsis style applied
     * @param container
     * @returns {{}}
     */
    getTheElementHavingEllipsis(container) {
		let elementHavingEllipsis = null;
		const traverse = (domElement) => {
			if(this.havingEllipsis(domElement)) {
				elementHavingEllipsis = domElement;
			}
			for (var i = 0; domElement.childNodes && i < domElement.childNodes.length; i++) {
				var child = domElement.childNodes[i];
				if(child.nodeType !== Node.TEXT_NODE && child.nodeType !== Node.COMMENT_NODE) {
					traverse(child);
				}
			}
		};
		traverse(container);
		return elementHavingEllipsis;
    };

    /**
     * Element having text overflow as ellipsis
     * @param child
     * @returns {boolean}
     */
    havingEllipsis (child){
        let computedStyle = window.getComputedStyle(child);
        return computedStyle.textOverflow === 'ellipsis';
    };


    render () {
		const { chevron, content, placement, className, showOnlyForTruncatedText, show, ...otherProps } = this.props;
		const additionalAttributes = {
			className: cx('i-flex', className),
			onMouseEnter: this.handleMouseEnter,
			onFocus: this.handleMouseEnter,
			onMouseLeave: this.handleMouseLeave,
			onBlur: this.handleMouseLeave,
			onClick: this.handleMouseLeave,
			ref: this.setUpRef,
			...otherProps
		};
		return (Helpers.wrapAndAddAdditionalAttributes(this, additionalAttributes));
	}
}

Tooltip.propTypes = {
	/**
	 * Configuration to control the chevron visibility
	 */
	chevron: PropTypes.bool,
	/**
	 * Class to be applied on the tooltip div
	 */
	className: PropTypes.string,
	/**
	 * Configuration to control alignment on the placement element
	 */
	placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
	/**
	 * Text to be displayed inside the tooltip
	 */
	content: PropTypes.string.isRequired,

    /**
	 * To display tooltip only for the element which has ellipsis applied and it is truncating text and displaying three dots(...)
     */
    showOnlyForTruncatedText: PropTypes.bool,

    /**
     * conditionally show/hide tooltips
     */
    show: PropTypes.bool
};

Tooltip.defaultProps = {
	className: '',
	chevron: false,
	placement: 'bottom',
    showOnlyForTruncatedText: false,
    show: true
};
